import {useEffect, useMemo} from 'react';
import Head from 'next/head';
import type {AppProps} from 'next/app';
import {useReportWebVitals} from 'next/web-vitals';
import {appWithTranslation} from 'next-i18next';
import configNextI18Next from 'next-i18next.config';
import {config} from '@modules/config';
import {
	buildFontFaceCss,
	getFontUrl,
	PRELOAD_FONT,
} from '@modules/assets/fonts';

import {AppRoot} from '@components/AppRoot';

const App = ({Component, pageProps}: AppProps) => {
	const fontFaceCss = useMemo(() => buildFontFaceCss(), []);
	const preloadHref = getFontUrl(PRELOAD_FONT.woff2);
	const assetsOrigin = (() => {
		try {
			return new URL(preloadHref).origin;
		} catch {
			return null;
		}
	})();

	useReportWebVitals((metrics) => {
		// eslint-disable-next-line no-console
		console.log('Metric:', metrics);
	});

	useEffect(() => {
		if (document.domain !== 'localhost') {
			try {
				// Used for some old browsers to enable some CORS stuff
				document.domain = config.get('host_cookie').slice(1);
			} catch {
				// noop
			}
		}
	}, []);

	return (
		<>
			<Head>
				{assetsOrigin && (
					<>
						<link
							rel="preconnect"
							href={assetsOrigin}
							crossOrigin="anonymous"
						/>
					</>
				)}

				<link
					rel="preload"
					as="font"
					type="font/woff2"
					href={preloadHref}
					crossOrigin="anonymous"
				/>
				<style
					// @font-face rules for Deezer Product live here so the URLs
					// can be constructed at runtime from black-keys config
					// (`host_commons_assets_ssl`) instead of being hardcoded.
					dangerouslySetInnerHTML={{__html: fontFaceCss}}
				/>
			</Head>
			<AppRoot>
				<Component {...pageProps} />
			</AppRoot>
		</>
	);
};

export default appWithTranslation(App, configNextI18Next);
